import { useState, useMemo } from 'react'
import { router } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Combobox } from '@/components/ui/combobox'
import { formatCurrency } from '@/lib/utils'
import { useBasePath } from '@/lib/paths'
import { FieldInputs, validateFields } from '@/components/applicant/FieldInputs'
import { ArrowRight, Clock, DollarSign, MapPin, Check } from 'lucide-react'

type CountryOption = { id: string; code: string; name: string }
type StateOption = { id: string; code: string; name: string; country_id: string }
type BusinessOption = { id: string; name: string; country_id: string | null; state_id: string | null }

type LicenseTypeOption = {
  id: string; code: string; name: string
  jurisdiction_code: string
  country_id: string | null; state_id: string | null
  gov_fee_cents: number; service_fee_cents: number; total_cents: number
  frequency: string | null; validity_period_months: number | null
  processing_days_min: number | null; processing_days_max: number | null
  fields: import('@/types').FieldDefinition[]
}

type Props = {
  countries: CountryOption[]
  states: StateOption[]
  types: LicenseTypeOption[]
  businesses: BusinessOption[]
}

function StepHeader({ step, title, done }: { step: number; title: string; done?: boolean }) {
  return (
    <div className="flex items-center gap-2.5 mb-3">
      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${done ? 'bg-foreground text-background' : 'bg-muted text-muted-foreground'}`}>
        {done ? <Check className="w-3.5 h-3.5" /> : step}
      </span>
      <h2 className="text-sm font-semibold text-foreground">{title}</h2>
    </div>
  )
}

export default function LicensesNew({ countries, states, types, businesses }: Props) {
  const basePath = useBasePath()
  const [selectedCountryId, setSelectedCountryId] = useState('')
  const [selectedStateId, setSelectedStateId] = useState('')
  const [selectedBusinessId, setSelectedBusinessId] = useState('')
  const [selectedTypeId, setSelectedTypeId] = useState('')
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [fieldValues, setFieldValues] = useState<Record<string, string | number | string[]>>({})
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const countryStates = states.filter(s => s.country_id === selectedCountryId)
  const hasStates = countryStates.length > 0

  const filteredTypes = useMemo(() => {
    if (!selectedCountryId) return []
    return types.filter(t => {
      if (t.country_id !== selectedCountryId) return false
      if (t.state_id) {
        if (!selectedStateId) return false
        if (t.state_id !== selectedStateId) return false
      }
      return true
    })
  }, [types, selectedCountryId, selectedStateId])

  const selectedType = types.find(t => t.id === selectedTypeId)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedBusinessId || !selectedTypeId || !selectedType) return

    const errors = validateFields(selectedType.fields, fieldValues)
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      return
    }

    setIsSubmitting(true)
    router.post(`${basePath}/licenses`, {
      license: {
        business_id: selectedBusinessId,
        license_type_id: selectedTypeId,
        start_date: startDate,
        field_values: fieldValues,
      }
    }, {
      onFinish: () => setIsSubmitting(false),
    })
  }

  const selectedBusiness = businesses.find(b => b.id === selectedBusinessId)
  const jurisdictionAutoSelected = !!selectedBusiness?.country_id
  const jurisdictionCountry = countries.find(c => c.id === (selectedBusiness?.country_id || selectedCountryId))
  const jurisdictionState = states.find(s => s.id === (selectedBusiness?.state_id || selectedStateId))

  // Sequential step numbers
  let step = 1
  const businessStep = step++
  const jurisdictionStep = step++
  const typeStep = step++
  const startDateStep = step++
  const hasFields = selectedType && selectedType.fields.length > 0
  const fieldsStep = hasFields ? step++ : 0
  const reviewStep = step

  return (
    <DashboardLayout title="New License" breadcrumbs={[{ label: 'Licenses', href: 'licenses' }, { label: 'New' }]}>
      <div className="mb-8">
        <h1 className="text-xl font-semibold tracking-tight">New License</h1>
        <p className="text-sm text-muted-foreground mt-1">Select a license from the catalog and apply it to your business</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Step 1: Business */}
        <section className="space-y-3">
          <StepHeader step={businessStep} title="Business" done={!!selectedBusinessId} />
          <Combobox
            options={businesses.map(b => ({ value: b.id, label: b.name }))}
            value={selectedBusinessId}
            onValueChange={(value) => {
              setSelectedBusinessId(value)
              const biz = businesses.find(b => b.id === value)
              if (biz?.country_id) {
                setSelectedCountryId(biz.country_id)
                setSelectedStateId(biz.state_id || '')
              } else {
                setSelectedCountryId('')
                setSelectedStateId('')
              }
              setSelectedTypeId('')
            }}
            placeholder="Select business..."
            searchPlaceholder="Search businesses..."
            emptyMessage="No businesses found."
          />
        </section>

        {/* Step 2: Jurisdiction */}
        {selectedBusinessId && (
          <section className="space-y-3">
            <StepHeader step={jurisdictionStep} title="Jurisdiction" done={!!selectedCountryId && (!hasStates || !!selectedStateId)} />
            {jurisdictionAutoSelected ? (
              <div className="flex items-center gap-2.5 rounded-lg border border-border bg-muted/50 px-4 py-3">
                <MapPin className="w-4 h-4 text-muted-foreground shrink-0" />
                <span className="text-sm text-foreground font-medium">
                  {[jurisdictionState?.name, jurisdictionCountry?.name].filter(Boolean).join(', ')}
                </span>
                <span className="text-xs text-muted-foreground ml-auto">Auto-selected from business</span>
              </div>
            ) : (
              <div className={`grid gap-3 ${hasStates ? 'grid-cols-2' : 'grid-cols-1'}`}>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Country</Label>
                  <Combobox
                    options={countries.map(c => ({ value: c.id, label: c.name }))}
                    value={selectedCountryId}
                    onValueChange={(v) => { setSelectedCountryId(v); setSelectedStateId(''); setSelectedTypeId('') }}
                    placeholder="Select country..."
                    searchPlaceholder="Search countries..."
                    emptyMessage="No countries found."
                  />
                </div>
                {hasStates && (
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">State</Label>
                    <Combobox
                      options={countryStates.map(s => ({ value: s.id, label: s.name }))}
                      value={selectedStateId}
                      onValueChange={(v) => { setSelectedStateId(v); setSelectedTypeId('') }}
                      placeholder="Select state..."
                      searchPlaceholder="Search states..."
                      emptyMessage="No states found."
                    />
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {/* Step 3: License Type */}
        {filteredTypes.length > 0 && (
          <section className="space-y-3">
            <StepHeader step={typeStep} title="License Type" done={!!selectedTypeId} />
            <div className="space-y-2">
              {filteredTypes.map(t => {
                const isSelected = selectedTypeId === t.id
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => { setSelectedTypeId(t.id); setFieldValues({}); setFieldErrors({}) }}
                    className={`w-full rounded-lg border px-4 py-3 text-left transition-all ${isSelected ? 'border-foreground bg-muted ring-1 ring-foreground/10' : 'border-border hover:border-muted-foreground/30 hover:bg-muted/30'}`}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <span className="text-sm font-medium text-foreground">{t.name}</span>
                      <span className="text-sm font-semibold text-foreground tabular-nums shrink-0">{formatCurrency(t.total_cents)}</span>
                    </div>
                    <div className="flex items-center gap-4 mt-1.5 text-xs text-muted-foreground">
                      <span className="capitalize">{t.frequency || 'One-time'}</span>
                      {t.processing_days_min && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {t.processing_days_min}–{t.processing_days_max} days
                        </span>
                      )}
                    </div>
                  </button>
                )
              })}
            </div>
          </section>
        )}

        {/* Step 4: Start Date */}
        {selectedType && (
          <section className="space-y-3">
            <StepHeader step={startDateStep} title="Start Date" done={!!startDate} />
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">When should this license obligation begin?</Label>
              <Input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
              />
            </div>
          </section>
        )}

        {/* Step 5 (conditional): Additional Fields */}
        {hasFields && (
          <section className="space-y-3">
            <StepHeader step={fieldsStep} title="Additional Information" />
            <FieldInputs
              fields={selectedType.fields}
              values={fieldValues}
              onChange={(fieldId, value) => setFieldValues(prev => ({ ...prev, [fieldId]: value }))}
              errors={fieldErrors}
            />
          </section>
        )}

        {/* Review & Submit */}
        {selectedType && selectedBusinessId && (
          <section className="space-y-3">
            <StepHeader step={reviewStep} title="Review & Submit" />

            <div className="rounded-xl border border-border bg-muted/40 p-5 space-y-4">
              <div className="space-y-2.5 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-foreground font-medium">{selectedType.name}</span>
                  <span className="capitalize text-xs text-muted-foreground px-2 py-0.5 rounded-full bg-muted border border-border">{selectedType.frequency || 'One-time'}</span>
                </div>
                <div className="space-y-1.5 pt-1">
                  <div className="flex justify-between text-muted-foreground text-xs">
                    <span>Government Fee</span>
                    <span className="tabular-nums">{formatCurrency(selectedType.gov_fee_cents)}</span>
                  </div>
                  <div className="flex justify-between text-muted-foreground text-xs">
                    <span>Service Fee</span>
                    <span className="tabular-nums">{formatCurrency(selectedType.service_fee_cents)}</span>
                  </div>
                </div>
                <div className="h-px bg-border" />
                <div className="flex justify-between font-semibold text-foreground">
                  <span>Total per Period</span>
                  <span className="tabular-nums">{formatCurrency(selectedType.total_cents)}</span>
                </div>
              </div>

              <Button type="submit" className="w-full h-10 text-sm font-medium" disabled={isSubmitting}>
                {isSubmitting ? 'Creating...' : 'Create License'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </section>
        )}
      </form>
    </DashboardLayout>
  )
}
